  <template>
  <div class="home-page">
    <!-- Banner Section -->
    <section class="banner"
             @mouseenter="stopBannerLoop"
             @mouseleave="startBannerLoop">
      <transition-group name="fade"
                        tag="div"
                        class="banner-group">
        <div v-for="(banner, index) in bannerList"
             :key="index"
             v-show="currentBannerIndex === index"
             class="banner-item">
          <div class="banner-bg"
               :style="{ backgroundImage: banner.image }"></div>
          <div class="banner-content-wrapper">
            <div class="banner-text">
              <h2 class="banner-title">{{ banner.title }}</h2>
              <p class="banner-desc">{{ banner.desc }}</p>
            </div>
          </div>
        </div>
      </transition-group>

      <!-- Indicators -->
      <div class="banner-indicators">
        <span v-for="(banner, index) in bannerList"
              :key="index"
              class="indicator-dot"
              :class="{ active: currentBannerIndex === index }"
              @click="setBanner(index)">
        </span>
      </div>
    </section>

    <!-- Teaching Resources Section -->
    <section class="resources-section">
      <h2 class="section-title">{{ $t('home.resources.title') }}</h2>

      <!-- Tabs -->
      <div class="tabs-container">
        <div class="tabs-wrapper">
          <div v-for="(tab, index) in tabs"
               :key="index"
               class="tab-item"
               :class="{ active: activeTab === index }"
               @click="activeTab = index">
            {{ tab.name }}
          </div>
          <!-- 蓝色激活条 -->
          <div class="active-bar"
               :style="{ transform: `translateX(${activeTab * 300}px)` }"></div>
        </div>
      </div>

      <!-- Carousel Content -->
      <div class="carousel-viewport">
        <div class="carousel-track"
             :style="trackStyle">
          <div v-for="item in displayItems"
               :key="item._uid"
               class="carousel-item"
               :class="{ active: activeTab === item._originalIndex }"
               @click="activeTab = item._originalIndex">
            <!-- 上方图片 -->
            <div class="item-image-wrapper">
              <div class="item-image"
                   :style="{ backgroundImage: item.image }"></div>
            </div>
            <!-- 下方文字 -->
            <div class="item-info"
                 :style="{ backgroundColor: item.color }">
              <div class="info-left">
                <h3 class="info-title">{{ item.title }}</h3>
              </div>
              <div class="info-divider"></div>
              <div class="info-right">
                <p class="info-desc">{{ item.desc1 }}</p>
                <p class="info-desc">{{ item.desc2 }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 与众不同板块 (Different Section) -->
    <section class="different-section">
      <h2 class="section-title">{{ $t('home.different.title') }}</h2>
      <div class="features-wrapper">
        <div v-for="(feature, index) in features"
             :key="index"
             class="feature-item">
          <div class="feature-icon-wrapper">
            <img :src="feature.icon"
                 alt="icon"
                 class="feature-icon" />
          </div>
          <div class="feature-text">
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-desc">{{ feature.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 创意板块 (Creative Section) -->
    <section class="creative-section">
      <div class="creative-container">
        <h2 class="section-title">{{ $t('home.creative.title') }}</h2>
        <div class="creative-card"
             @mouseenter="stopCreativeLoop"
             @mouseleave="startCreativeLoop">
          <transition-group name="fade"
                            tag="div"
                            class="creative-group">
            <div v-for="(slide, index) in creativeSlides"
                 :key="slide.id"
                 v-show="currentCreativeIndex === index"
                 class="creative-slide">
              <div class="card-top">
                <div class="machine-info">
                  <h3 class="machine-name">{{ slide.name }}</h3>
                  <p class="machine-desc">{{ $t(slide.descKey) }}</p>
                </div>
                <img :src="slide.machineImage"
                     :alt="slide.name"
                     class="machine-image" />
              </div>
              <div class="card-bottom">
                <img :src="slide.galleryImage"
                     :alt="slide.name"
                     class="gallery-image" />
              </div>
            </div>
          </transition-group>
        </div>
      </div>
    </section>

    <!-- 创新板块 (Innovation Section) -->
    <section class="innovation-section">
      <div class="innovation-header">
        <h2 class="section-title">{{ $t('home.innovation.title') }}</h2>
        <p class="section-desc">{{ $t('home.innovation.desc') }}</p>
      </div>
      <div class="innovation-grid">
        <div v-for="(item, index) in innovationList"
             :key="index"
             class="innovation-item">
          <div class="innovation-image"
               :style="{ backgroundImage: item.image }"></div>
          <div class="innovation-overlay">
            <span class="innovation-title">{{ item.title }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 底部 Banner 板块 -->
    <section class="bottom-banner">
      <div class="bottom-banner-content">
        <!-- 这里可以放参考图中的内容，目前先做背景和布局 -->
        <div class="bottom-banner-left">
          <!-- 模拟左侧3D图 -->
          <img src="@/assets/home/aibanner.png"
               class="bottom-banner-icon"
               alt="AI Education" />
        </div>
        <div class="bottom-banner-right">
          <h2 class="bottom-banner-title">{{ $t('home.bottomBanner.title') }}</h2>
          <h3 class="bottom-banner-subtitle">{{ $t('home.bottomBanner.subtitle') }}</h3>
          <p class="bottom-banner-desc">{{ $t('home.bottomBanner.desc') }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  data() {
    return {
      activeTab: 0,
      windowWidth: window.innerWidth,
      // Banner Data
      currentBannerIndex: 0,
      bannerTimer: null,
      currentCreativeIndex: 0,
      creativeTimer: null
    }
  },
  computed: {
    bannerList() {
      return [
        {
          title: this.$t('home.banner.item1.title'),
          desc: this.$t('home.banner.item1.desc'),
          image: `url(${require('@/assets/home/banner1.png')})`
        },
        {
          title: this.$t('home.banner.item2.title'),
          desc: this.$t('home.banner.item2.desc'),
          image: `url(${require('@/assets/home/banner2.png')})`
        },
        {
          title: this.$t('home.banner.item3.title'),
          desc: this.$t('home.banner.item3.desc'),
          image: `url(${require('@/assets/home/banner3.png')})`
        }
      ]
    },
    innovationList() {
      return [
        {
          title: this.$t('home.innovation.items.steam'),
          image: `url(${require('@/assets/home/innovation1.png')})`
        },
        {
          title: this.$t('home.innovation.items.coding'),
          image: `url(${require('@/assets/home/innovation2.png')})`
        },
        {
          title: this.$t('home.innovation.items.light530'),
          image: `url(${require('@/assets/home/innovation3.png')})`
        },
        {
          title: this.$t('home.innovation.items.robotics'),
          image: `url(${require('@/assets/home/innovation4.png')})`
        }
      ]
    },
    features() {
      return [
        {
          title: this.$t('home.different.items.solution.title'),
          desc: this.$t('home.different.items.solution.desc'),
          icon: require('@/assets/home/yuz1.png')
        },
        {
          title: this.$t('home.different.items.course.title'),
          desc: this.$t('home.different.items.course.desc'),
          icon: require('@/assets/home/yuz2.png')
        },
        {
          title: this.$t('home.different.items.product.title'),
          desc: this.$t('home.different.items.product.desc'),
          icon: require('@/assets/home/yuz3.png')
        },
        {
          title: this.$t('home.different.items.space.title'),
          desc: this.$t('home.different.items.space.desc'),
          icon: require('@/assets/home/yuz4.png')
        }
      ]
    },
    creativeSlides() {
      return [
        {
          id: 'light530',
          name: 'Light 530',
          descKey: 'home.creative.machineDesc',
          machineImage: require('@/assets/home/light530.png'),
          galleryImage: require('@/assets/home/creative.png')
        },
        {
          id: 'lightblue530',
          name: 'Light Blue 530',
          descKey: 'home.creative.lightBlue530Desc',
          machineImage: require('@/assets/home/LightBlue530.png'),
          galleryImage: require('@/assets/home/Light530Bluebanner.png')
        },
        {
          id: 'markm20',
          name: 'Mark M20',
          descKey: 'home.creative.markM20Desc',
          machineImage: require('@/assets/home/MarkM20.png'),
          galleryImage: require('@/assets/home/M20banner .png')
        },
        {
          id: 'light640',
          name: 'Light 640',
          descKey: 'home.creative.light640Desc',
          machineImage: require('@/assets/home/640.png'),
          galleryImage: require('@/assets/home/Light640banner.png')
        }
      ]
    },
    tabs() {
      return [
        {
          name: this.$t('home.resources.tabs.laser.name'),
          title: this.$t('home.resources.tabs.laser.title'),
          desc1: this.$t('home.resources.tabs.laser.desc1'),
          desc2: this.$t('home.resources.tabs.laser.desc2'),
          color: '#4285F4',
          image: `url(${require('@/assets/home/edu1.png')})`
        },
        {
          name: this.$t('home.resources.tabs.software.name'),
          title: this.$t('home.resources.tabs.software.title'),
          desc1: this.$t('home.resources.tabs.software.desc1'),
          desc2: this.$t('home.resources.tabs.software.desc2'),
          color: '#EA4335',
          image: `url(${require('@/assets/home/edu2.png')})`
        },
        {
          name: this.$t('home.resources.tabs.ai.name'),
          title: this.$t('home.resources.tabs.ai.title'),
          desc1: this.$t('home.resources.tabs.ai.desc1'),
          desc2: this.$t('home.resources.tabs.ai.desc2'),
          color: '#92D300',
          image: `url(${require('@/assets/home/edu3.png')})`
        },
        {
          name: this.$t('home.resources.tabs.kits.name'),
          title: this.$t('home.resources.tabs.kits.title'),
          desc1: this.$t('home.resources.tabs.kits.desc1'),
          desc2: this.$t('home.resources.tabs.kits.desc2'),
          color: '#FBBC05',
          image: `url(${require('@/assets/home/edu4.png')})`
        }
      ]
    },
    displayItems() {
      // 前后补位，解决首尾空白问题
      if (this.tabs.length === 0) return [];

      const last = { ...this.tabs[this.tabs.length - 1], _uid: 'prev-last', _originalIndex: this.tabs.length - 1 };
      const first = { ...this.tabs[0], _uid: 'next-first', _originalIndex: 0 };
      const middles = this.tabs.map((item, index) => ({ ...item, _uid: `orig-${index}`, _originalIndex: index }));

      return [last, ...middles, first];
    },
    trackStyle() {
      // 计算偏移量让当前 activeItem 居中
      // displayItems 结构: [Last, Tab0, Tab1, Tab2, Tab3, First]
      // Tab0 对应的索引是 1
      // TabIndex 对应的索引是 TabIndex + 1

      const itemWidth = 1200;
      const gap = 20;
      const centerOffset = (this.windowWidth - itemWidth) / 2;

      const displayIndex = this.activeTab + 1;
      const itemPosition = displayIndex * (itemWidth + gap);
      const translateX = centerOffset - itemPosition;

      return {
        transform: `translateX(${translateX}px)`
      };
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.startBannerLoop();
    this.startCreativeLoop();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.stopBannerLoop();
    this.stopCreativeLoop();
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    // Banner Methods
    startBannerLoop() {
      this.stopBannerLoop();
      this.bannerTimer = setInterval(() => {
        this.nextBanner();
      }, 5000);
    },
    stopBannerLoop() {
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer);
        this.bannerTimer = null;
      }
    },
    nextBanner() {
      this.currentBannerIndex = (this.currentBannerIndex + 1) % this.bannerList.length;
    },
    setBanner(index) {
      this.currentBannerIndex = index;
    },
    startCreativeLoop() {
      this.stopCreativeLoop();
      this.creativeTimer = setInterval(() => {
        this.nextCreative();
      }, 5000);
    },
    stopCreativeLoop() {
      if (this.creativeTimer) {
        clearInterval(this.creativeTimer);
        this.creativeTimer = null;
      }
    },
    nextCreative() {
      this.currentCreativeIndex = (this.currentCreativeIndex + 1) % this.creativeSlides.length;
    }
  }
}
</script>

<style scoped>
.home-page {
  width: 100%;
  overflow-x: hidden;
  background-color: #fff;
}

/* Banner */
.banner {
  height: 880px;
  position: relative;
  width: 100%;
  background-color: #000; /* 兜底背景色 */
}

.banner-group {
  width: 100%;
  height: 100%;
  position: relative;
}

.banner-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.banner-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  filter: brightness(0.8);
  transition: transform 6s ease; /* 可选：增加微缩放效果 */
}

/* 简单的淡入淡出动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 激活时，图片微缩放，增加动感 */
.banner-item.active .banner-bg {
  transform: scale(1.05);
}

.banner-content-wrapper {
  position: relative;
  width: 1200px;
  height: 100%;
  margin: 0 auto;
  pointer-events: none;
  z-index: 2; /* 确保文字在图片之上 */
}

.banner-text {
  position: absolute;
  bottom: 160px;
  left: -60px;
  color: #fff;
  text-align: left;
  pointer-events: auto;
}

.banner-title {
  font-size: 40px;
  font-weight: normal;
  margin: 0 0 20px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.banner-desc {
  font-size: 24px;
  margin: 0;
  opacity: 0.9;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.banner-indicators {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 15px;
  z-index: 10;
}

.indicator-dot {
  width: 50px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 2px;
}

.indicator-dot.active {
  background-color: #fff;
  width: 70px;
}

/* Resources Section */
.resources-section {
  padding: 60px 0 100px;
}

.section-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 50px;
}

/* Tabs */
.tabs-container {
  width: 100%;
  margin-bottom: 60px;
}

.tabs-wrapper {
  width: 1200px;
  margin: 0 auto;
  display: flex;
  position: relative;
}

/* 底部灰色长条背景 - 实际上是横条，可以作为 tabs-wrapper 的伪元素或者子元素 */
.tabs-wrapper::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: #f8f8f8;
  z-index: 0;
}

.tab-item {
  width: 300px;
  text-align: center;
  font-size: 20px;
  color: #333;
  padding-bottom: 20px; /* 给下划线留位置 */
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: color 0.3s;
}

.tab-item.active {
  color: #333;
  font-weight: 500;
}

.active-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 300px;
  height: 4px;
  background-color: #4285f4;
  z-index: 2;
  transition: transform 0.3s ease;
}

/* Carousel */
.carousel-viewport {
  width: 100%;
  overflow: hidden; /* 溢出隐藏 */
  padding: 20px 0; /* 上下留点白 */
}

.carousel-track {
  display: flex;
  gap: 20px; /* 左右间距 20px */
  transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  /* 初始位置由 JS 计算 */
}

.carousel-item {
  flex: 0 0 1200px; /* 固定宽度 1200 */
  width: 1200px;
  height: 537px;
  display: flex;
  flex-direction: column;
  transition: opacity 0.3s, transform 0.3s;
  cursor: pointer;
}

.item-image-wrapper {
  width: 1200px;
  height: 412px;
  background-color: #eee;
  overflow: hidden;
}

.item-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: transform 0.5s ease;
}

.item-image:hover {
  transform: scale(1.05);
}

.item-info {
  width: 1200px;
  height: 125px;
  display: flex;
  align-items: center;
  padding: 0 40px; /* 内部边距 */
  box-sizing: border-box;
}

.info-left {
  flex-shrink: 0;
}

.info-title {
  font-size: 24px;
  color: #fff;
  font-weight: normal; /* 不加粗 */
  margin: 0;
}

.info-divider {
  width: 2px;
  height: 74px;
  background-color: rgba(255, 255, 255, 1);
  margin: 0 80px; /* 分割线左右间距 */
}

.info-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.info-desc {
  font-size: 16px;
  color: #fff;
  margin: 0;
  line-height: 1.4;
}

/* Different Section */
.different-section {
  width: 1200px;
  margin: 0 auto 100px;
}

.features-wrapper {
  display: flex;
  justify-content: space-between;
}

.feature-item {
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.feature-icon-wrapper {
  width: 175px;
  height: 175px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.feature-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.feature-text {
  width: 178px;
  /* height: 57px; 高度可以是自动撑开 */
  margin-top: 20px;
}

.feature-title {
  font-size: 20px;
  color: #333;
  font-weight: normal;
  margin: 0 0 8px 0;
}

.feature-desc {
  font-size: 16px;
  color: #666;
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
}

/* Creative Section */
.creative-section {
  width: 100%;
  height: 1469px;
  background-color: #f4f6fa;
  padding-top: 60px; /* 标题上方留白 */
  box-sizing: border-box;
}

.creative-container {
  width: 1200px;
  margin: 0 auto;
}

.creative-card {
  width: 1200px;
  height: 1075px;
  border-radius: 32px 32px 0 0;
  overflow: hidden;
  margin-top: 50px; /* 标题下方间距 */
}

.creative-group {
  width: 100%;
  height: 100%;
  position: relative;
}

.creative-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.card-top {
  width: 1200px;
  height: 599px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* 垂直居中 */
}

.machine-info {
  text-align: center;
  margin-bottom: 40px; /* 图片上方间距 */
}

.machine-name {
  font-size: 20px;
  font-weight: normal;
  color: #333;
  margin: 0 0 10px 0;
}

.machine-desc {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.machine-image {
  width: 434px;
  height: 288px;
  object-fit: contain;
}

.card-bottom {
  width: 1200px;
  height: 476px;
  /* 这里的图片应该占满 */
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Innovation Section */
.innovation-section {
  width: 1200px;
  margin: 100px auto;
  text-align: center;
}

.innovation-header {
  margin-bottom: 50px;
}

.innovation-section .section-desc {
  font-size: 18px;
  color: #666;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.6;
}

.innovation-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 36px;
}

.innovation-item {
  width: 582px;
  height: 361px;
  border-radius: 66px;
  position: relative;
  overflow: hidden;
  background-color: #e0e0e0; /* 增加背景色防止图片加载失败时不可见 */
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05); /* 增加阴影 */
}

.innovation-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.innovation-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 582px;
  height: 74px;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.innovation-title {
  font-size: 20px;
  color: #fff;
  font-weight: normal;
}

/* Bottom Banner */
.bottom-banner {
  width: 100%;
  height: 600px;
  background: linear-gradient(180deg, #e4f3ff 0%, #b5dfff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.bottom-banner-content {
  width: 1200px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 80px;
}

.bottom-banner-left {
  flex: 0 0 auto;
}

.bottom-banner-icon {
  width: 500px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(66, 133, 244, 0.2));
}

.bottom-banner-right {
  flex: 0 0 auto;
  text-align: left;
}

.bottom-banner-title {
  font-size: 34px;
  font-weight: bold;
  color: #333;
  margin: 0 0 10px 0;
}

.bottom-banner-subtitle {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0 0 20px 0;
}

.bottom-banner-desc {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}
</style>
